import React, { useState, useEffect, useCallback } from "react";
import { useQuery } from "@apollo/client";
import axios from "axios";
import {
  GetNewLessonsLtiDocument,
  GetNewLessonsLtiQuery,
} from "../../generated/graphql";
import { useResetPageOnSearch } from "../../hooks";
import { CircularProgress, Container, Table } from "@material-ui/core";
import { Menu } from "../shared/Menu";
import {
  StyledTableContainer,
  StyledTableRow,
  StyledTableCell,
  StyledTableHeader,
  StyledSearchInput,
  StyledPaginationContainer,
  StyledPaginationButton,
  StyledPaginationInfo,
  LoadingContainer,
  ErrorContainer,
  EmptyContainer,
} from "./LessonList.styles";
import Cookies from "js-cookie";
import { COOKIE_NAME_TOKEN } from "../../constants/cookies";

interface SearchFilter {
  searchKey?: string;
}

export const LessonList: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [inputValue, setInputValue] = useState("");
  const [activeFilter, setActiveFilter] = useState<SearchFilter>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectLessonError, setSelectLessonError] = useState<string | null>(
    null
  );
  const [ltik, setLtik] = useState<string | null>(null);

  useEffect(() => {
    const token = Cookies.get(COOKIE_NAME_TOKEN);
    if (token) {
      setLtik(token);
    }
  }, []);

  const searchKey =
    activeFilter.searchKey && activeFilter.searchKey.length >= 4
      ? activeFilter.searchKey
      : undefined;

  useResetPageOnSearch({ searchKey, setPage });

  const getLessonsData = {
    limit: rowsPerPage,
    skip: page * rowsPerPage,
    searchKey,
  };

  const { data, loading, error } = useQuery<GetNewLessonsLtiQuery>(
    GetNewLessonsLtiDocument,
    {
      variables: { ...getLessonsData },
      fetchPolicy: "cache-and-network",
      skip: !ltik,
    }
  );

  const debounce = <T extends (...args: any[]) => void>(
    func: T,
    delay: number
  ): ((...args: Parameters<T>) => void) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedSetFilter = useCallback(
    debounce((value: string) => {
      setActiveFilter({
        searchKey: value || undefined,
      });
    }, 300),
    []
  );

  useEffect(() => {
    debouncedSetFilter(inputValue);
  }, [inputValue, debouncedSetFilter]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handlePreviousPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    const totalLessons = data?.getNewLessonsLti?.lessons?.length || 0;
    if (totalLessons === rowsPerPage) {
      setPage(page + 1);
    }
  };

  const handleSelectLesson = async (lesson: any) => {
    if (!ltik) {
      setSelectLessonError("LTI token is not available. Cannot proceed.");
      return;
    }
    try {
      setIsSubmitting(true);
      setSelectLessonError(null);

      const response = await axios.post(
        `${process.env.REACT_APP_AR_ARBOOK_LTI_SERVER_URL}/lti/select`,
        {
          selectedItems: [
            {
              id: lesson.id,
              title: lesson.name,
              isGradable: false,
            },
          ],
        },
        {
          headers: {
            Authorization: `Bearer ${ltik}`,
            "Content-Type": "application/json",
          },
          responseType: "text",
        }
      );

      const formHtml = response.data;

      const container = document.createElement("div");
      container.innerHTML = formHtml;

      const form = container.querySelector("form");

      if (form) {
        document.body.appendChild(form);
        form.submit();
      } else {
        throw new Error(
          "Could not find a form in the response from the server."
        );
      }
    } catch (err) {
      console.error("Error during lesson selection or form submission:", err);
      const errorMessage =
        err instanceof Error ? err.message : "An unknown error occurred.";
      setSelectLessonError(`Failed to select the lesson. ${errorMessage}`);
      setIsSubmitting(false);
    }
  };

  const menuItems = (lesson: any) => [
    {
      name: "Выбрать урок",
      action: () => handleSelectLesson(lesson),
      disabled: isSubmitting || !ltik,
    },
  ];

  if (isSubmitting) {
    return (
      <LoadingContainer>
        <CircularProgress color="inherit" />
        <p>Завершение выбора, возвращаемся в LMS...</p>
      </LoadingContainer>
    );
  }

  const renderTableContent = () => {
    if (loading) {
      return (
        <tbody>
          <StyledTableRow>
            <StyledTableCell colSpan={2}>
              <LoadingContainer>
                <CircularProgress color="inherit" />
              </LoadingContainer>
            </StyledTableCell>
          </StyledTableRow>
        </tbody>
      );
    }

    if (error) {
      return (
        <tbody>
          <StyledTableRow>
            <StyledTableCell colSpan={2}>
              <ErrorContainer>
                При загрузке уроков произошла ошибка: {error.message}
              </ErrorContainer>
            </StyledTableCell>
          </StyledTableRow>
        </tbody>
      );
    }

    if (selectLessonError) {
      return (
        <tbody>
          <StyledTableRow>
            <StyledTableCell colSpan={2}>
              <ErrorContainer>{selectLessonError}</ErrorContainer>
            </StyledTableCell>
          </StyledTableRow>
        </tbody>
      );
    }

    if (!data?.getNewLessonsLti?.lessons?.length) {
      return (
        <tbody>
          <StyledTableRow>
            <StyledTableCell colSpan={2}>
              <EmptyContainer>Список уроков пуст</EmptyContainer>
            </StyledTableCell>
          </StyledTableRow>
        </tbody>
      );
    }

    const lessons = data.getNewLessonsLti.lessons;

    return (
      <>
        <thead>
          <StyledTableRow>
            <StyledTableHeader>Название урока</StyledTableHeader>
            <StyledTableHeader>Действия</StyledTableHeader>
          </StyledTableRow>
        </thead>
        <tbody>
          {lessons.map((lesson: any) => (
            <StyledTableRow key={lesson.id}>
              <StyledTableCell>{lesson.name}</StyledTableCell>
              <StyledTableCell>
                <Menu
                  vertical={36}
                  horizontal={12}
                  width={150}
                  disabled={isSubmitting || !ltik}
                  items={menuItems(lesson)}
                />
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </tbody>
      </>
    );
  };

  const renderPagination = () => {
    const totalLessons = data?.getNewLessonsLti?.lessons?.length || 0;
    const startIndex = page * rowsPerPage + 1;
    const endIndex = page * rowsPerPage + totalLessons;

    return (
      <StyledPaginationContainer>
        <StyledPaginationInfo>
          Показано {startIndex}-{endIndex} записей
        </StyledPaginationInfo>
        <div>
          <StyledPaginationButton
            onClick={handlePreviousPage}
            disabled={page === 0 || isSubmitting}
          >
            Назад
          </StyledPaginationButton>
          <StyledPaginationButton
            onClick={handleNextPage}
            disabled={totalLessons < rowsPerPage || isSubmitting}
          >
            Далее
          </StyledPaginationButton>
        </div>
      </StyledPaginationContainer>
    );
  };

  return (
    <Container maxWidth="lg">
      <h1>Список уроков LTI</h1>
      <StyledSearchInput
        type="text"
        placeholder="Поиск по названию урока (минимум 4 символа)"
        value={inputValue}
        onChange={handleSearchChange}
        disabled={isSubmitting}
      />
      {data?.getNewLessonsLti?.lessons && renderPagination()}
      <StyledTableContainer>
        <Table>{renderTableContent()}</Table>
      </StyledTableContainer>
      {data?.getNewLessonsLti?.lessons && renderPagination()}
    </Container>
  );
};
